import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { FiActivity, FiGlobe, FiMapPin, FiSliders } from 'react-icons/fi';
import { TERRITORY_COUNTRIES, getTerritoryCountry, getProvinceFeatureTerritory, resolveTerritory } from '../utils/territories';
import { addLeafletCompassControl } from '../utils/leafletCompassControl';
import { buildNeighborCountryOutlines } from '../utils/countryOutlines';
import { DASHBOARD_DEFAULT_ZOOM, INTA_CERRILLOS_CENTER, INTA_CERRILLOS_LABEL } from '../utils/mapDefaults';

const DEFAULT_CENTER = INTA_CERRILLOS_CENTER;
const DEFAULT_ZOOM = DASHBOARD_DEFAULT_ZOOM;
const LEVEL_FOCUS_ZOOM = {
  provincia: 7,
  departamento: 9,
};
const DEPARTMENTS_GEOJSON_URL = `${process.env.PUBLIC_URL || ''}/maps/departamentos/departamentos.geojson`;

const PROVINCE_CODE_LABELS = {
  '02': 'Ciudad Autónoma de Buenos Aires',
  '06': 'Buenos Aires',
  '10': 'Catamarca',
  '14': 'Córdoba',
  '18': 'Corrientes',
  '22': 'Chaco',
  '26': 'Chubut',
  '30': 'Entre Ríos',
  '34': 'Formosa',
  '38': 'Jujuy',
  '42': 'La Pampa',
  '46': 'La Rioja',
  '50': 'Mendoza',
  '54': 'Misiones',
  '58': 'Neuquén',
  '62': 'Río Negro',
  '66': 'Salta',
  '70': 'San Juan',
  '74': 'San Luis',
  '78': 'Santa Cruz',
  '82': 'Santa Fe',
  '86': 'Santiago del Estero',
  '90': 'Tucumán',
  '94': 'Tierra del Fuego',
};

const formatNumber = (value) => new Intl.NumberFormat('es-AR').format(value || 0);
const formatPercent = (value) => `${Math.round((Number(value) || 0) * 100)}%`;

const parseCoordinate = (value) => {
  if (value === '' || value === null || value === undefined) {
    return null;
  }

  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
};

const escapeHtml = (value = '') =>
  String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');

const normalizeMapKey = (value = '') =>
  String(value)
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .toUpperCase();

const normalizeSpeciesLabel = (value) => {
  if (!value) {
    return 'Sin especie';
  }

  return String(value)
    .toLowerCase()
    .split('_')
    .map((chunk) => (chunk ? `${chunk[0].toUpperCase()}${chunk.slice(1)}` : chunk))
    .join(' ');
};

const getHeatColor = (ratio) => {
  if (ratio >= 0.85) {
    return '#0c5ea8';
  }
  if (ratio >= 0.65) {
    return '#187dc4';
  }
  if (ratio >= 0.45) {
    return '#2d9cdb';
  }
  if (ratio >= 0.25) {
    return '#62bee8';
  }
  return '#9ee3ff';
};

const getCaseSpeciesLabels = (caso = {}) => {
  const species = new Set();
  const muestras = Array.isArray(caso.muestras) ? caso.muestras : [];

  muestras.forEach((muestra) => {
    species.add(normalizeSpeciesLabel(muestra?.grupo_zootecnico));
  });

  if (species.size === 0) {
    species.add('Sin especie');
  }

  return Array.from(species);
};

const getGeoJsonFeatures = (geoJson) => (Array.isArray(geoJson?.features) ? geoJson.features : []);

const getProvinceFeatureLabel = (feature = {}) =>
  feature?.properties?.NIVEL_9
  || feature?.properties?.nombre
  || feature?.properties?.provincia
  || 'Provincia';

const getDepartmentRawCode = (feature = {}) => String(feature?.properties?.in1 || '').trim();

const getDepartmentProvinceLabel = (feature = {}) => {
  const provinceCode = getDepartmentRawCode(feature).replace(/\D/g, '').slice(0, 2);
  return PROVINCE_CODE_LABELS[provinceCode] || '';
};

const getDepartmentName = (feature = {}) =>
  feature?.properties?.nam
  || feature?.properties?.fna
  || feature?.properties?.nombre
  || 'Departamento sin nombre';

const getDepartmentFeatureId = (feature = {}) => {
  const rawCode = getDepartmentRawCode(feature);
  if (rawCode) {
    return `departamento-${rawCode}`;
  }

  return `departamento-${feature?.properties?.gid || normalizeMapKey(getDepartmentName(feature))}`;
};

const getDepartmentFeatureLabel = (feature = {}) => {
  const departmentName = getDepartmentName(feature);
  const provinceLabel = getDepartmentProvinceLabel(feature);
  return provinceLabel ? `${departmentName}, ${provinceLabel}` : departmentName;
};

const forEachCoordinate = (coordinates, callback) => {
  if (!Array.isArray(coordinates)) {
    return;
  }

  if (typeof coordinates[0] === 'number' && typeof coordinates[1] === 'number') {
    callback(coordinates);
    return;
  }

  coordinates.forEach((child) => forEachCoordinate(child, callback));
};

const getGeometryBounds = (geometry) => {
  let minLng = Infinity;
  let minLat = Infinity;
  let maxLng = -Infinity;
  let maxLat = -Infinity;

  forEachCoordinate(geometry?.coordinates, ([lng, lat]) => {
    if (!Number.isFinite(lng) || !Number.isFinite(lat)) {
      return;
    }

    minLng = Math.min(minLng, lng);
    minLat = Math.min(minLat, lat);
    maxLng = Math.max(maxLng, lng);
    maxLat = Math.max(maxLat, lat);
  });

  if (![minLng, minLat, maxLng, maxLat].every(Number.isFinite)) {
    return null;
  }

  return { minLng, minLat, maxLng, maxLat };
};

const getBoundsCenter = (bounds) => {
  if (!bounds) {
    return null;
  }

  return {
    latitud: Number(((bounds.minLat + bounds.maxLat) / 2).toFixed(6)),
    longitud: Number(((bounds.minLng + bounds.maxLng) / 2).toFixed(6)),
  };
};

const isPointInBounds = ([lng, lat], bounds) =>
  Boolean(bounds) && lng >= bounds.minLng && lng <= bounds.maxLng && lat >= bounds.minLat && lat <= bounds.maxLat;

const isPointInRing = ([lng, lat], ring = []) => {
  let inside = false;

  for (let index = 0, previousIndex = ring.length - 1; index < ring.length; previousIndex = index, index += 1) {
    const current = ring[index];
    const previous = ring[previousIndex];
    if (!current || !previous) {
      continue;
    }

    const currentLng = current[0];
    const currentLat = current[1];
    const previousLng = previous[0];
    const previousLat = previous[1];
    const intersects =
      (currentLat > lat) !== (previousLat > lat)
      && lng < ((previousLng - currentLng) * (lat - currentLat)) / (previousLat - currentLat) + currentLng;

    if (intersects) {
      inside = !inside;
    }
  }

  return inside;
};

const isPointInPolygon = (point, polygonCoordinates = []) => {
  if (!isPointInRing(point, polygonCoordinates[0] || [])) {
    return false;
  }

  return !(polygonCoordinates.slice(1) || []).some((hole) => isPointInRing(point, hole));
};

const isPointInGeometry = (point, geometry = {}) => {
  if (geometry.type === 'Polygon') {
    return isPointInPolygon(point, geometry.coordinates);
  }

  if (geometry.type === 'MultiPolygon') {
    return (geometry.coordinates || []).some((polygon) => isPointInPolygon(point, polygon));
  }

  return false;
};

const prepareDepartmentFeatures = (departmentGeoJson) =>
  getGeoJsonFeatures(departmentGeoJson).map((feature) => {
    const bounds = getGeometryBounds(feature.geometry);

    return {
      id: getDepartmentFeatureId(feature),
      label: getDepartmentFeatureLabel(feature),
      departamento: getDepartmentName(feature),
      provincia: getDepartmentProvinceLabel(feature),
      bounds,
      centro: getBoundsCenter(bounds),
      feature,
    };
  });
const buildAreaFromAccumulator = (accumulator) => {
  const speciesRanking = Array.from(accumulator.speciesCounts.entries()).sort(
    (left, right) => right[1] - left[1] || left[0].localeCompare(right[0])
  );
  const [dominantSpecies = 'Sin especie', dominantCount = 0] = speciesRanking[0] || [];

  return {
    id: accumulator.id,
    label: accumulator.label,
    provincia: accumulator.provincia,
    departamento: accumulator.departamento,
    casos: accumulator.casos,
    casos_con_coordenadas: accumulator.casos,
    casos_sin_coordenadas: 0,
    centro: accumulator.centro,
    especie_predominante: dominantSpecies,
    especie_predominante_casos: dominantCount,
    especie_predominante_porcentaje: accumulator.casos > 0 ? dominantCount / accumulator.casos : 0,
    top_especies: speciesRanking.slice(0, 5).map(([label, casos]) => ({
      label,
      casos,
      porcentaje: accumulator.casos > 0 ? casos / accumulator.casos : 0,
    })),
  };
};

const buildDepartmentAreas = (casos, departmentFeatures) => {
  const accumulators = new Map();
  const featuresWithBounds = departmentFeatures.filter((featureInfo) => featureInfo.bounds);
  let casesWithoutCoordinates = 0;
  let casesOutsideLayer = 0;

  (Array.isArray(casos) ? casos : []).forEach((caso) => {
    if (String(caso.pais || '').trim() && getTerritoryCountry(caso.pais)?.code !== 'ARG') return;
    const latitud = parseCoordinate(caso.latitud);
    const longitud = parseCoordinate(caso.longitud);

    if (latitud === null || longitud === null) {
      casesWithoutCoordinates += 1;
      return;
    }

    const point = [longitud, latitud];
    const matchedFeature = featuresWithBounds.find(
      (featureInfo) => isPointInBounds(point, featureInfo.bounds) && isPointInGeometry(point, featureInfo.feature.geometry)
    );

    if (!matchedFeature) {
      casesOutsideLayer += 1;
      return;
    }

    const accumulator = accumulators.get(matchedFeature.id) || {
      id: matchedFeature.id,
      label: matchedFeature.label,
      provincia: matchedFeature.provincia,
      departamento: matchedFeature.departamento,
      centro: matchedFeature.centro,
      casos: 0,
      speciesCounts: new Map(),
    };

    accumulator.casos += 1;
    getCaseSpeciesLabels(caso).forEach((speciesLabel) => {
      accumulator.speciesCounts.set(speciesLabel, (accumulator.speciesCounts.get(speciesLabel) || 0) + 1);
    });
    accumulators.set(matchedFeature.id, accumulator);
  });

  const areas = Array.from(accumulators.values())
    .map(buildAreaFromAccumulator)
    .sort((left, right) => right.casos - left.casos || left.label.localeCompare(right.label));

  return {
    areas,
    summary: {
      total_areas: areas.length,
      total_areas_con_centro: areas.length,
      total_casos: areas.reduce((total, area) => total + area.casos, 0),
      max_casos: areas[0]?.casos || 0,
      total_casos_sin_coordenadas: casesWithoutCoordinates,
      total_casos_fuera_capa: casesOutsideLayer,
    },
  };
};

const buildPopupHtml = (area, fallbackLabel = '') => {
  if (!area) {
    return `
      <div class="territorial-map-popup">
        <strong>${escapeHtml(fallbackLabel || 'Área sin coincidencia')}</strong><br/>
        Sin casos visibles para el rango actual.
      </div>
    `;
  }

  const topSpecies = Array.isArray(area.top_especies) ? area.top_especies : [];
  const speciesMarkup = topSpecies.length > 0
    ? topSpecies
        .map(
          (species) =>
            `<li><strong>${escapeHtml(species.label)}</strong>: ${formatNumber(species.casos)} caso${species.casos === 1 ? '' : 's'}</li>`
        )
        .join('')
    : '<li>Sin especies registradas</li>';

  return `
    <div class="territorial-map-popup">
      <strong>${escapeHtml(area.label)}</strong><br/>
      Casos: ${formatNumber(area.casos)}<br/>
      Especie predominante: ${escapeHtml(area.especie_predominante || 'Sin especie')}<br/>
      Cobertura predominante: ${formatPercent(area.especie_predominante_porcentaje)}<br/>
      Con coordenadas: ${formatNumber(area.casos_con_coordenadas)}<br/>
      <ul style="margin:8px 0 0; padding-left:18px;">${speciesMarkup}</ul>
    </div>
  `;
};

const emptySummary = {
  total_areas: 0,
  total_areas_con_centro: 0,
  total_casos: 0,
  max_casos: 0,
  total_casos_sin_coordenadas: 0,
  total_casos_fuera_capa: 0,
};

export const buildResearchProvinceAreas = (casos, geoJson = null, countryFilter = 'ALL') => {
  const features = getGeoJsonFeatures(geoJson).map((feature) => ({
    feature, territory: getProvinceFeatureTerritory(feature), bounds: getGeometryBounds(feature.geometry),
  })).filter((entry) => entry.territory && entry.bounds);
  const groups = new Map();
  casos.forEach((caso) => {
    let territory = resolveTerritory(caso);
    const lat = parseCoordinate(caso.latitud);
    const lng = parseCoordinate(caso.longitud);
    const hasCoordinates = lat !== null && lng !== null;
    if (hasCoordinates) {
      const explicitCountry = String(caso.pais || '').trim() ? getTerritoryCountry(caso.pais)?.code || 'UNSUPPORTED' : null;
      const matches = features.filter((entry) => (!explicitCountry || entry.territory.pais_codigo === explicitCountry)
        && isPointInBounds([lng, lat], entry.bounds) && isPointInGeometry([lng, lat], entry.feature.geometry));
      const ids = new Set(matches.map((entry) => entry.territory.id));
      if (ids.size === 1) territory = matches[0].territory;
    }
    if (countryFilter !== 'ALL' && territory.pais_codigo !== countryFilter) return;
    const { id } = territory;
    const group = groups.get(id) || {
      ...territory, label: `${territory.provincia}, ${territory.pais}`,
      casos: 0, speciesCounts: new Map(), coordinates: 0,
    };
    group.casos += 1;
    if (hasCoordinates) group.coordinates += 1;
    getCaseSpeciesLabels(caso).forEach((species) => group.speciesCounts.set(species, (group.speciesCounts.get(species) || 0) + 1));
    groups.set(id, group);
  });
  const featureIds = new Set(features.map((entry) => entry.territory.id));
  const areas = Array.from(groups.values()).map((group) => ({
    ...buildAreaFromAccumulator(group), pais: group.pais, pais_codigo: group.pais_codigo,
    provincia_codigo: group.provincia_codigo, casos_con_coordenadas: group.coordinates,
    casos_sin_coordenadas: group.casos - group.coordinates,
  })).sort((a, b) => b.casos - a.casos || a.label.localeCompare(b.label));
  return { areas, summary: {
    ...emptySummary, total_areas: areas.length,
    total_casos: areas.reduce((sum, area) => sum + area.casos, 0), max_casos: areas[0]?.casos || 0,
    total_casos_sin_coordenadas: areas.reduce((sum, area) => sum + area.casos_sin_coordenadas, 0),
    total_casos_fuera_capa: areas.filter((area) => !featureIds.has(area.id)).reduce((sum, area) => sum + area.casos, 0),
  } };
};

const EMPTY_CASES = [];
const TerritorialHeatmap = ({ casos = EMPTY_CASES }) => {
  const [nivel, setNivel] = useState('provincia');
  const [countryFilter, setCountryFilter] = useState('ALL');
  const [selectedAreaId, setSelectedAreaId] = useState('');
  const [runtimeError, setRuntimeError] = useState('');
  const [provinceGeoJson, setProvinceGeoJson] = useState(null);
  const [provinceGeoJsonLoading, setProvinceGeoJsonLoading] = useState(true);
  const [provinceGeoJsonError, setProvinceGeoJsonError] = useState('');
  const [departmentGeoJson, setDepartmentGeoJson] = useState(null);
  const [departmentGeoJsonLoading, setDepartmentGeoJsonLoading] = useState(true);
  const [departmentGeoJsonError, setDepartmentGeoJsonError] = useState('');
  const mapContainerRef = useRef(null);
  const mapRef = useRef(null);
  const layerGroupRef = useRef(null);
  const areaLayerMapRef = useRef(new Map());

  const isProvinceMode = nivel === 'provincia';
  const countryOutlines = useMemo(() => buildNeighborCountryOutlines(provinceGeoJson), [provinceGeoJson]);

  useEffect(() => {
    let active = true;

    setProvinceGeoJsonLoading(true);
    setProvinceGeoJsonError('');

    const countries = TERRITORY_COUNTRIES.filter((country) => countryFilter === 'ALL' || country.code === countryFilter);
    setProvinceGeoJson(null);
    Promise.all(countries.map((country) => axios.get(`${process.env.PUBLIC_URL || ''}${country.geojson}`)))
      .then((responses) => {
        if (active) {
          setProvinceGeoJson({ type: 'FeatureCollection', features: responses.flatMap((response) => getGeoJsonFeatures(response.data)) });
        }
      })
      .catch((fetchError) => {
        console.error('Error al cargar los límites de provincias:', fetchError);
        if (active) {
          setProvinceGeoJson(null);
          setProvinceGeoJsonError('No se pudo cargar la capa de provincias para el mapa territorial.');
        }
      })
      .finally(() => {
        if (active) {
          setProvinceGeoJsonLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [countryFilter]);

  useEffect(() => {
    if (nivel !== 'departamento') return undefined;
    let active = true;

    setDepartmentGeoJsonLoading(true);
    setDepartmentGeoJsonError('');

    axios
      .get(DEPARTMENTS_GEOJSON_URL)
      .then((response) => {
        if (active) {
          setDepartmentGeoJson(response.data || null);
        }
      })
      .catch((fetchError) => {
        console.error('Error al cargar los límites de departamentos:', fetchError);
        if (active) {
          setDepartmentGeoJson(null);
          setDepartmentGeoJsonError('No se pudo cargar la capa de departamentos para el mapa territorial.');
        }
      })
      .finally(() => {
        if (active) {
          setDepartmentGeoJsonLoading(false);
        }
      });

    return () => {
      active = false;
    };
  }, [nivel]);
  const provinceStats = useMemo(
    () => buildResearchProvinceAreas(casos, provinceGeoJson, countryFilter),
    [casos, provinceGeoJson, countryFilter]
  );
  const provinceAreas = provinceStats.areas;
  const provinceSummary = provinceStats.summary;

  const departmentFeatures = useMemo(() => prepareDepartmentFeatures(departmentGeoJson), [departmentGeoJson]);
  const departmentStats = useMemo(() => buildDepartmentAreas(casos, departmentFeatures), [casos, departmentFeatures]);
  const areas = isProvinceMode ? provinceAreas : departmentStats.areas;
  const summary = isProvinceMode ? provinceSummary : departmentStats.summary;
  const activeGeoJson = isProvinceMode ? provinceGeoJson : departmentGeoJson;
  const activeGeoJsonLoading = isProvinceMode ? provinceGeoJsonLoading : departmentGeoJsonLoading;
  const activeGeoJsonError = isProvinceMode ? provinceGeoJsonError : departmentGeoJsonError;
  const activeGeoJsonFeatures = getGeoJsonFeatures(activeGeoJson);
  const hasActiveShapes = activeGeoJsonFeatures.length > 0;

  const provinceAreasByKey = useMemo(() => {
    const nextMap = new Map();

    provinceAreas.forEach((area) => {
      const key = area.id;
      if (key) {
        nextMap.set(key, area);
      }
    });

    return nextMap;
  }, [provinceAreas]);

  const provinceFeatureKeys = useMemo(() => {
    const nextSet = new Set();

    getGeoJsonFeatures(provinceGeoJson).forEach((feature) => {
      const key = getProvinceFeatureTerritory(feature)?.id;
      if (key) {
        nextSet.add(key);
      }
    });

    return nextSet;
  }, [provinceGeoJson]);

  const departmentAreasById = useMemo(() => {
    const nextMap = new Map();

    departmentStats.areas.forEach((area) => {
      nextMap.set(area.id, area);
    });

    return nextMap;
  }, [departmentStats.areas]);

  const departmentFeatureIds = useMemo(() => new Set(departmentFeatures.map((featureInfo) => featureInfo.id)), [departmentFeatures]);

  useEffect(() => {
    if (selectedAreaId && areas.some((area) => area.id === selectedAreaId)) {
      return;
    }

    setSelectedAreaId(areas[0]?.id || '');
  }, [areas, selectedAreaId]);

  const selectedArea = useMemo(
    () => areas.find((area) => area.id === selectedAreaId) || null,
    [areas, selectedAreaId]
  );

  const selectedAreaHasMapSupport = useMemo(() => {
    if (!selectedArea) {
      return false;
    }

    if (isProvinceMode) {
      return provinceFeatureKeys.has(selectedArea.id);
    }

    return departmentFeatureIds.has(selectedArea.id);
  }, [departmentFeatureIds, isProvinceMode, provinceFeatureKeys, selectedArea]);

  const isMapLoading = activeGeoJsonLoading;
  const activeMapError = runtimeError || activeGeoJsonError;
  const shouldShowMap = !isMapLoading && !activeMapError && hasActiveShapes && areas.length > 0;

  useEffect(() => {
    if (!shouldShowMap) {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        layerGroupRef.current = null;
        areaLayerMapRef.current = new Map();
      }
      return undefined;
    }

    if (!mapContainerRef.current || mapRef.current) {
      return undefined;
    }

    if (!window.L) {
      setRuntimeError('No se pudo cargar la libreria del mapa territorial.');
      return undefined;
    }

    const map = window.L.map(mapContainerRef.current, {
      center: DEFAULT_CENTER,
      zoom: DEFAULT_ZOOM,
      scrollWheelZoom: true,
    });
    const countryOutlinePane = map.createPane('countryOutlines');
    countryOutlinePane.style.zIndex = '450';
    countryOutlinePane.style.pointerEvents = 'none';

    window.L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: `&copy; OpenStreetMap contributors | <a href="https://www.geoboundaries.org/">geoBoundaries</a> | <a href="${process.env.PUBLIC_URL || ''}/maps/territories/ATTRIBUTION.html">Fuentes de límites</a>`,
    }).addTo(map);
    addLeafletCompassControl(window.L, map, {
      title: 'Brujula: el mapa mantiene el norte hacia arriba.',
    });

    layerGroupRef.current = window.L.layerGroup().addTo(map);
    mapRef.current = map;

    window.setTimeout(() => {
      map.invalidateSize();
    }, 0);

    return () => {
      map.remove();
      mapRef.current = null;
      layerGroupRef.current = null;
      areaLayerMapRef.current = new Map();
    };
  }, [shouldShowMap]);

  useEffect(() => {
    const map = mapRef.current;
    const layerGroup = layerGroupRef.current;
    if (!shouldShowMap || !map || !layerGroup || !window.L) {
      return;
    }

    layerGroup.clearLayers();
    areaLayerMapRef.current = new Map();

    const resolveFeatureArea = (feature) => {
      if (isProvinceMode) {
        const label = getProvinceFeatureLabel(feature);
        const area = provinceAreasByKey.get(getProvinceFeatureTerritory(feature)?.id) || null;
        const territory = getProvinceFeatureTerritory(feature);
        return { area, fallbackLabel: territory ? `${territory.provincia}, ${territory.pais}` : label };
      }

      const featureId = getDepartmentFeatureId(feature);
      const area = departmentAreasById.get(featureId) || null;
      return { area, fallbackLabel: getDepartmentFeatureLabel(feature) };
    };

    const maxCasos = Math.max(...areas.map((area) => area.casos), 1);
    const bounds = window.L.latLngBounds([]);

    const geoJsonLayer = window.L.geoJSON(activeGeoJson, {
      style: (feature) => {
        const { area } = resolveFeatureArea(feature);
        const ratio = area ? area.casos / maxCasos : 0;
        const isSelected = area?.id === selectedAreaId;

        return {
          color: isSelected ? '#083a6b' : '#0c5ea8',
          weight: isSelected ? 3 : area ? 1.6 : 1,
          fillColor: area ? getHeatColor(ratio) : '#dfe9f2',
          fillOpacity: area ? (isSelected ? 0.9 : 0.76) : 0.18,
          dashArray: area ? null : '4 5',
        };
      },
      onEachFeature: (feature, layer) => {
        const { area, fallbackLabel } = resolveFeatureArea(feature);

        layer.bindPopup(buildPopupHtml(area, fallbackLabel));
        layer.on('click', () => {
          if (area) {
            setSelectedAreaId(area.id);
          }
        });

        if (area) {
          const areaLayers = areaLayerMapRef.current.get(area.id) || [];
          areaLayers.push(layer);
          areaLayerMapRef.current.set(area.id, areaLayers);

          if (area.casos > 0 && typeof layer.getBounds === 'function') {
            bounds.extend(layer.getBounds());
          }
        }
      },
    });

    geoJsonLayer.addTo(layerGroup);
    window.L.geoJSON(countryOutlines, {
      pane: 'countryOutlines',
      interactive: false,
      style: { color: '#dc2626', weight: 1.5, opacity: 0.65, fill: false },
    }).addTo(layerGroup);

    if (selectedArea) {
      const selectedLayers = areaLayerMapRef.current.get(selectedArea.id) || [];
      if (selectedLayers.length > 0) {
        const selectedBounds = window.L.latLngBounds([]);
        selectedLayers.forEach((layer) => {
          if (typeof layer.getBounds === 'function') {
            selectedBounds.extend(layer.getBounds());
          }
          if (typeof layer.bringToFront === 'function') {
            layer.bringToFront();
          }
        });

        if (selectedBounds.isValid()) {
          map.fitBounds(selectedBounds.pad(0.08));
          selectedLayers[0]?.openPopup();
          return;
        }
      }
    }

    if (bounds.isValid()) {
      map.fitBounds(bounds.pad(0.16));
      return;
    }

    map.setView(DEFAULT_CENTER, DEFAULT_ZOOM);
  }, [
    activeGeoJson,
    countryOutlines,
    areas,
    departmentAreasById,
    isProvinceMode,
    provinceAreasByKey,
    selectedArea,
    selectedAreaId,
    shouldShowMap,
  ]);

  const handleFocusArea = (area) => {
    setSelectedAreaId(area.id);

    const map = mapRef.current;
    const layers = areaLayerMapRef.current.get(area.id) || [];
    if (!map || layers.length === 0 || !window.L) {
      return;
    }

    const selectedBounds = window.L.latLngBounds([]);
    layers.forEach((layer) => {
      if (typeof layer.getBounds === 'function') {
        selectedBounds.extend(layer.getBounds());
      }
    });

    if (selectedBounds.isValid()) {
      map.fitBounds(selectedBounds.pad(0.08));
      layers[0]?.openPopup();
      return;
    }

    if (area.centro) {
      map.setView([area.centro.latitud, area.centro.longitud], LEVEL_FOCUS_ZOOM[nivel]);
      layers[0]?.openPopup();
    }
  };

  const topArea = areas[0] || null;
  const casesWithoutDepartment = (summary.total_casos_sin_coordenadas || 0) + (summary.total_casos_fuera_capa || 0);
  return (
    <section className="analytics-section territorial-section" aria-busy={isMapLoading}>
      <div className="analytics-section__header territorial-section__header">
        <div className="territorial-section__heading">
          <span className="section-kicker">Territorio</span>
          <h3 className="page-title analytics-section__title">Mapa de calor territorial</h3>
          <p className="page-subtitle">
            Compara los casos por provincia, estado, región o departamento de Argentina y sus países limítrofes.
          </p>
        </div>

      </div>

      <div className="surface-card case-list-card territorial-toolbar-card">
        <div className="dashboard-filter-toolbar territorial-toolbar">
          <label className="territorial-level-selector">
            <span className="territorial-level-selector__label">País del mapa</span>
            <select className="form-select" value={countryFilter} onChange={(event) => {
              setCountryFilter(event.target.value);
              if (event.target.value !== 'ARG') setNivel('provincia');
            }}>
              <option value="ALL">Todos los países</option>
              {TERRITORY_COUNTRIES.map((country) => <option key={country.code} value={country.code}>{country.name}</option>)}
            </select>
          </label>
          <label className="territorial-level-selector">
            <span className="territorial-level-selector__label">
              <FiSliders aria-hidden="true" />
              Elegir vista territorial
            </span>
            <select className="form-select territorial-level-selector__select" value={nivel} onChange={(event) => setNivel(event.target.value)}>
              <option value="provincia">Provincia / Estado / Región / Departamento</option>
              <option value="departamento" disabled={countryFilter !== 'ARG'}>Departamentos de Argentina (segundo nivel)</option>
            </select>
            <span className="territorial-level-selector__hint">Haz clic para cambiar</span>
          </label>

          <div className="case-time-bubble">
            <span className="case-time-bubble__label">Lectura del mapa</span>
            <strong>{formatNumber(summary.total_casos)} casos agregados</strong>
            <span className="territorial-toolbar__microcopy">
              {isProvinceMode
                ? `${formatNumber(summary.total_areas)} divisiones para el rango actual. ${formatNumber(summary.total_casos_fuera_capa)} casos sin polígono asociado.`
                : `${formatNumber(summary.total_areas)} departamentos con casos georreferenciados. ${formatNumber(casesWithoutDepartment)} casos no pudieron asignarse a un departamento.`}
            </span>
          </div>

          <div className="territorial-legend" aria-hidden="true">
            <span className="territorial-legend__label">Intensidad</span>
            <div className="territorial-legend__scale" />
            <div className="territorial-legend__ticks">
              <span>Baja</span>
              <span>Alta</span>
            </div>
          </div>
        </div>
      </div>

      <div className="dashboard-layout row g-3 territorial-layout">
        <div className="col-12 col-xxl-8">
          <div className="surface-card dashboard-map-panel territorial-map-panel">
            <div className="dashboard-map-panel__header">
              <div>
                <h4 className="form-title">Calor territorial</h4>
                <p className="page-subtitle analytics-panel__subtitle territorial-map-panel__subtitle">
                  El mapa inicia cerca de {INTA_CERRILLOS_LABEL} y ajusta el foco automáticamente con las zonas del rango seleccionado.
                </p>
              </div>
              <div className="territorial-map-panel__chips">
                <span className="protocol-chip protocol-chip--dashboard">{formatNumber(summary.total_areas)} zonas</span>
                {topArea ? <span className="protocol-chip territorial-chip">Pico: {topArea.label}</span> : null}
              </div>
            </div>

            {isMapLoading ? (
              <div className="dashboard-empty">Cargando mapa territorial...</div>
            ) : activeMapError ? (
              <div className="alert subtle-alert mb-0">{activeMapError}</div>
            ) : !hasActiveShapes ? (
              <div className="dashboard-empty">
                La capa de {isProvinceMode ? 'divisiones territoriales' : 'departamentos'} no está disponible todavía para el mapa.
              </div>
            ) : areas.length === 0 ? (
              <div className="dashboard-empty">
                {isProvinceMode
                  ? 'No hay divisiones con casos en el rango temporal seleccionado.'
                  : 'No hay departamentos con casos georreferenciados en el rango temporal seleccionado.'}
              </div>
            ) : (
              <div ref={mapContainerRef} className="dashboard-map territorial-map" />
            )}
          </div>
        </div>

        <div className="col-12 col-xxl-4">
          <aside className="surface-card dashboard-side-panel territorial-side-panel">
            <div className="dashboard-side-panel__top">
              <div>
                <h4 className="form-title">Zonas agrupadas</h4>
                <p className="page-subtitle territorial-side-panel__subtitle">
                  Selecciona una zona para enfocar el mapa y revisar la especie predominante.
                </p>
              </div>
              <span className="protocol-chip">{formatNumber(summary.total_areas)} visibles</span>
            </div>

            {selectedArea ? (
              <div className="dashboard-selected-card territorial-selected-card">
                <div className="dashboard-selected-card__actions">
                  <div>
                    <h5 className="dashboard-selected-card__title">{selectedArea.label}</h5>
                    <p className="dashboard-selected-card__meta">{formatNumber(selectedArea.casos)} casos en el período actual</p>
                  </div>
                  <span className="protocol-chip territorial-chip">
                    {selectedAreaHasMapSupport ? 'Polígono disponible' : 'Sin polígono asociado'}
                  </span>
                </div>

                <div className="territorial-selected-card__grid">
                  <article className="territorial-selected-card__item">
                    <span className="territorial-selected-card__label">Especie predominante</span>
                    <strong>{selectedArea.especie_predominante || 'Sin especie'}</strong>
                    <span>{formatPercent(selectedArea.especie_predominante_porcentaje)} del área</span>
                  </article>
                  <article className="territorial-selected-card__item">
                    <span className="territorial-selected-card__label">Cobertura georreferenciada</span>
                    <strong>{formatNumber(selectedArea.casos_con_coordenadas)}</strong>
                    <span>{formatNumber(selectedArea.casos_sin_coordenadas)} sin asignación territorial</span>
                  </article>
                </div>
              </div>
            ) : null}

            <div className="dashboard-side-panel__body">
              {areas.length > 3 ? (
                <p className="dashboard-list-hint d-md-none">Desliza la lista para ver más zonas.</p>
              ) : null}
              <div className="dashboard-case-list territorial-area-list" role="region" aria-label="Lista de zonas del mapa de calor" tabIndex={0}>
                {isMapLoading ? (
                  <div className="dashboard-empty dashboard-empty--compact">Cargando zonas...</div>
                ) : areas.length === 0 ? (
                  <div className="dashboard-empty dashboard-empty--compact">No hay agrupaciones para mostrar.</div>
                ) : (
                  areas.map((area) => {
                    const isActive = area.id === selectedArea?.id;
                    const isMappable = isProvinceMode
                      ? provinceFeatureKeys.has(area.id)
                      : departmentFeatureIds.has(area.id);

                    return (
                      <button
                        key={area.id}
                        type="button"
                        className={`territorial-area-item${isActive ? ' territorial-area-item--active' : ''}${isMappable ? '' : ' territorial-area-item--muted'}`}
                        aria-pressed={isActive}
                        onClick={() => handleFocusArea(area)}
                      >
                        <div className="territorial-area-item__top">
                          <strong>{area.label}</strong>
                          <span className="territorial-area-item__badge">{formatNumber(area.casos)}</span>
                        </div>
                        <span className="territorial-area-item__meta">
                          Especie predominante: {area.especie_predominante || 'Sin especie'}
                        </span>
                        <span className="territorial-area-item__meta territorial-area-item__detail">
                          {formatPercent(area.especie_predominante_porcentaje)} del total del área
                        </span>
                        <span className="territorial-area-item__meta territorial-area-item__detail">
                          {isMappable
                            ? (isProvinceMode ? 'Polígono territorial disponible' : 'Polígono departamental disponible')
                            : 'Sin polígono asociado en la capa'}
                        </span>
                      </button>
                    );
                  })
                )}
              </div>
            </div>
          </aside>
        </div>
      </div>

      <div className="territorial-summary-grid">
        <article className="territorial-summary-card">
          <span className="territorial-summary-card__icon" aria-hidden="true">
            <FiGlobe />
          </span>
          <strong>{formatNumber(summary.total_areas)}</strong>
          <span>{isProvinceMode ? 'Divisiones agregadas' : 'Departamentos con casos'}</span>
        </article>
        <article className="territorial-summary-card">
          <span className="territorial-summary-card__icon" aria-hidden="true">
            <FiActivity />
          </span>
          <strong>{formatNumber(summary.total_casos)}</strong>
          <span>Casos dentro de la vista</span>
        </article>
        <article className="territorial-summary-card">
          <span className="territorial-summary-card__icon" aria-hidden="true">
            <FiMapPin />
          </span>
          <strong>{formatNumber(isProvinceMode ? areas.filter((area) => provinceFeatureKeys.has(area.id)).length : casesWithoutDepartment)}</strong>
          <span>{isProvinceMode ? 'Divisiones con polígono' : 'Casos sin departamento'}</span>
        </article>
      </div>
    </section>
  );
};

export default TerritorialHeatmap;
